// Surface textures for box levels, drawn on canvases at load time (no image files, no game assets).
// One 256 px tile covers 128 world units. Each material has a pattern; the box colour tints it.

import * as THREE from 'three';
import { imageTexture } from '../assets/kenney';
import type { MATERIALS, MaterialName } from '../maps/boxlevel/types';

const SIZE = 256;

/** Small deterministic random generator so a material always looks the same. */
function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  const n = m ? parseInt(m[1], 16) : 0x888888;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const rgba = (c: [number, number, number], k: number, a = 1) =>
  `rgba(${Math.max(0, Math.min(255, Math.round(c[0] * k)))},${Math.max(0, Math.min(255, Math.round(c[1] * k)))},${Math.max(0, Math.min(255, Math.round(c[2] * k)))},${a})`;

function speckle(
  g: CanvasRenderingContext2D,
  c: [number, number, number],
  n: number,
  r: () => number,
  lo = 0.8,
  hi = 1.2,
) {
  for (let i = 0; i < n; i++) {
    g.fillStyle = rgba(c, lo + r() * (hi - lo), 0.5);
    const s = 1 + r() * 3;
    g.fillRect(r() * SIZE, r() * SIZE, s, s);
  }
}

/** Draw `name` tinted with `color`. Returns the colour canvas and, for glowing materials, a glow canvas. */
function draw(name: MaterialName, color: string): { map: HTMLCanvasElement; glow?: HTMLCanvasElement } {
  const c = hexToRgb(color);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  const g = canvas.getContext('2d')!;
  const r = rng(name.length * 7919 + c[0] * 31 + c[1] * 17 + c[2]);
  let glow: HTMLCanvasElement | undefined;
  const glowCtx = () => {
    glow = document.createElement('canvas');
    glow.width = glow.height = SIZE;
    const gg = glow.getContext('2d')!;
    gg.fillStyle = '#000';
    gg.fillRect(0, 0, SIZE, SIZE);
    return gg;
  };
  g.fillStyle = rgba(c, 1);
  g.fillRect(0, 0, SIZE, SIZE);

  switch (name) {
    case 'grid': {
      g.fillStyle = rgba(c, 0.75, 0.6);
      for (let i = 0; i < SIZE; i += 32) {
        g.fillRect(i, 0, 1, SIZE);
        g.fillRect(0, i, SIZE, 1);
      }
      g.fillStyle = rgba(c, 0.5, 0.8);
      for (let i = 0; i < SIZE; i += 128) {
        g.fillRect(i, 0, 3, SIZE);
        g.fillRect(0, i, SIZE, 3);
      }
      break;
    }
    case 'concrete': {
      speckle(g, c, 2500, r, 0.85, 1.12);
      g.strokeStyle = rgba(c, 0.7, 0.5);
      g.lineWidth = 2;
      g.strokeRect(1, 1, SIZE - 2, SIZE - 2);
      g.beginPath();
      g.moveTo(0, SIZE / 2);
      g.lineTo(SIZE, SIZE / 2);
      g.stroke();
      break;
    }
    case 'planks': {
      const rows = 8;
      const h = SIZE / rows;
      for (let i = 0; i < rows; i++) {
        g.fillStyle = rgba(c, 0.85 + r() * 0.3);
        g.fillRect(0, i * h, SIZE, h);
        // grain
        for (let k = 0; k < 7; k++) {
          g.strokeStyle = rgba(c, 0.7 + r() * 0.15, 0.45);
          g.lineWidth = 1;
          g.beginPath();
          const y = i * h + 3 + r() * (h - 6);
          g.moveTo(0, y);
          g.bezierCurveTo(SIZE * 0.3, y + r() * 4 - 2, SIZE * 0.6, y + r() * 4 - 2, SIZE, y);
          g.stroke();
        }
        g.fillStyle = rgba(c, 0.45);
        g.fillRect(0, i * h, SIZE, 2);
        const seam = (i % 2 ? 0.3 : 0.75) * SIZE;
        g.fillRect(seam, i * h, 2, h);
      }
      break;
    }
    case 'bricks': {
      const bh = 32;
      const bw = 64;
      g.fillStyle = rgba(c, 0.55);
      g.fillRect(0, 0, SIZE, SIZE);
      for (let y = 0; y < SIZE; y += bh)
        for (let x = -bw; x < SIZE; x += bw) {
          const ox = (y / bh) % 2 ? bw / 2 : 0;
          g.fillStyle = rgba(c, 0.85 + r() * 0.3);
          g.fillRect(x + ox + 2, y + 2, bw - 4, bh - 4);
        }
      speckle(g, c, 800, r, 0.7, 1);
      break;
    }
    case 'tiles': {
      const t = 64;
      g.fillStyle = rgba(c, 0.6);
      g.fillRect(0, 0, SIZE, SIZE);
      for (let y = 0; y < SIZE; y += t)
        for (let x = 0; x < SIZE; x += t) {
          g.fillStyle = rgba(c, 0.95 + r() * 0.12);
          g.fillRect(x + 2, y + 2, t - 4, t - 4);
          g.fillStyle = rgba(c, 1.25, 0.25);
          g.fillRect(x + 4, y + 4, t - 8, 6);
        }
      break;
    }
    case 'metal': {
      speckle(g, c, 600, r, 0.9, 1.1);
      // diamond plate
      for (let y = 0; y < SIZE; y += 32)
        for (let x = 0; x < SIZE; x += 32) {
          const ox = (y / 32) % 2 ? 16 : 0;
          g.save();
          g.translate(x + ox + 16, y + 16);
          g.rotate(((y / 32) % 2 ? 1 : -1) * 0.7);
          g.fillStyle = rgba(c, 1.35, 0.8);
          g.fillRect(-9, -2, 18, 4);
          g.fillStyle = rgba(c, 0.6, 0.8);
          g.fillRect(-9, 2, 18, 1);
          g.restore();
        }
      g.strokeStyle = rgba(c, 0.5);
      g.lineWidth = 3;
      g.strokeRect(1.5, 1.5, SIZE - 3, SIZE - 3);
      break;
    }
    case 'stone': {
      g.fillStyle = rgba(c, 0.5);
      g.fillRect(0, 0, SIZE, SIZE);
      for (let i = 0; i < 26; i++) {
        const x = r() * SIZE;
        const y = r() * SIZE;
        const rad = 18 + r() * 26;
        for (const [dx, dy] of [
          [0, 0],
          [SIZE, 0],
          [-SIZE, 0],
          [0, SIZE],
          [0, -SIZE],
        ]) {
          g.fillStyle = rgba(c, 0.8 + r() * 0.35);
          g.beginPath();
          g.ellipse(x + dx, y + dy, rad, rad * (0.7 + r() * 0.3), r() * 3, 0, Math.PI * 2);
          g.fill();
        }
      }
      speckle(g, c, 900, r, 0.7, 1.15);
      break;
    }
    case 'grass': {
      for (let i = 0; i < 5000; i++) {
        g.strokeStyle = rgba(c, 0.7 + r() * 0.6, 0.6);
        g.lineWidth = 1;
        const x = r() * SIZE;
        const y = r() * SIZE;
        g.beginPath();
        g.moveTo(x, y);
        g.lineTo(x + r() * 4 - 2, y - 3 - r() * 5);
        g.stroke();
      }
      break;
    }
    case 'sand': {
      speckle(g, c, 4000, r, 0.88, 1.12);
      g.strokeStyle = rgba(c, 0.82, 0.35);
      g.lineWidth = 2;
      for (let y = 10; y < SIZE; y += 22) {
        g.beginPath();
        for (let x = 0; x <= SIZE; x += 8) g.lineTo(x, y + Math.sin((x / SIZE) * Math.PI * 4 + y) * 4);
        g.stroke();
      }
      break;
    }
    case 'ice': {
      const grad = g.createLinearGradient(0, 0, SIZE, SIZE);
      grad.addColorStop(0, rgba(c, 1.15));
      grad.addColorStop(1, rgba(c, 0.9));
      g.fillStyle = grad;
      g.fillRect(0, 0, SIZE, SIZE);
      g.strokeStyle = rgba([255, 255, 255], 1, 0.55);
      for (let i = 0; i < 14; i++) {
        g.lineWidth = 0.5 + r() * 1.5;
        g.beginPath();
        let x = r() * SIZE;
        let y = r() * SIZE;
        g.moveTo(x, y);
        for (let k = 0; k < 5; k++) {
          x += r() * 50 - 25;
          y += r() * 50 - 25;
          g.lineTo(x, y);
        }
        g.stroke();
      }
      break;
    }
    case 'neon': {
      g.fillStyle = rgba(c, 0.18);
      g.fillRect(0, 0, SIZE, SIZE);
      g.strokeStyle = rgba(c, 0.32);
      g.lineWidth = 1;
      for (let i = 0; i < SIZE; i += 32) {
        g.strokeRect(i, 0, 32, SIZE);
        g.strokeRect(0, i, SIZE, 32);
      }
      const gg = glowCtx();
      for (const ctx of [g, gg]) {
        ctx.strokeStyle = rgba(c, 1.2);
        ctx.lineWidth = 6;
        ctx.strokeRect(3, 3, SIZE - 6, SIZE - 6);
        ctx.lineWidth = 3;
        ctx.strokeRect(SIZE / 2 - 30, SIZE / 2 - 30, 60, 60);
      }
      break;
    }
    case 'lava': {
      g.fillStyle = rgba(c, 0.25);
      g.fillRect(0, 0, SIZE, SIZE);
      const gg = glowCtx();
      for (let i = 0; i < 24; i++) {
        let x = r() * SIZE;
        let y = r() * SIZE;
        for (const ctx of [g, gg]) {
          ctx.strokeStyle = ctx === g ? rgba(c, 1.4) : rgba(c, 1.2);
          ctx.lineWidth = 2 + r() * 3;
          ctx.beginPath();
          ctx.moveTo(x, y);
        }
        for (let k = 0; k < 4; k++) {
          x += r() * 60 - 30;
          y += r() * 60 - 30;
          g.lineTo(x, y);
          gg.lineTo(x, y);
        }
        g.stroke();
        gg.stroke();
      }
      speckle(g, c, 1200, r, 0.15, 0.45);
      break;
    }
    case 'crystal': {
      for (let i = 0; i < 40; i++) {
        g.fillStyle = rgba(c, 0.75 + r() * 0.6, 0.85);
        g.beginPath();
        const x = r() * SIZE;
        const y = r() * SIZE;
        g.moveTo(x, y);
        g.lineTo(x + r() * 80 - 40, y + r() * 80 - 40);
        g.lineTo(x + r() * 80 - 40, y + r() * 80 - 40);
        g.fill();
      }
      break;
    }
    case 'water': {
      // Ripples.
      g.strokeStyle = rgba(c, 1.35, 0.55);
      g.lineWidth = 3;
      for (let y = 16; y < SIZE; y += 32) {
        g.beginPath();
        for (let x = 0; x <= SIZE; x += 8) g.lineTo(x, y + Math.sin((x / SIZE) * Math.PI * 4 + y) * 5);
        g.stroke();
      }
      break;
    }
    case 'wind': {
      // Chevrons pointing along the tile.
      g.fillStyle = rgba(c, 0.6, 1);
      g.fillRect(0, 0, SIZE, SIZE);
      g.strokeStyle = rgba(c, 1.4, 0.9);
      g.lineWidth = 10;
      for (let y = 32; y < SIZE; y += 96) {
        g.beginPath();
        g.moveTo(64, y + 32);
        g.lineTo(128, y);
        g.lineTo(192, y + 32);
        g.stroke();
      }
      break;
    }
    case 'ladder': {
      // Rails and rungs on a dark backing.
      g.fillStyle = 'rgba(20,18,16,1)';
      g.fillRect(0, 0, SIZE, SIZE);
      g.fillStyle = rgba(c, 1);
      g.fillRect(24, 0, 24, SIZE);
      g.fillRect(SIZE - 48, 0, 24, SIZE);
      for (let y = 16; y < SIZE; y += 64) g.fillRect(24, y, SIZE - 48, 18);
      break;
    }
    case 'glass': {
      g.fillStyle = rgba(c, 1.1);
      g.fillRect(0, 0, SIZE, SIZE);
      g.strokeStyle = rgba([255, 255, 255], 1, 0.6);
      g.lineWidth = 4;
      g.strokeRect(2, 2, SIZE - 4, SIZE - 4);
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(30, 60);
      g.lineTo(90, 0);
      g.moveTo(40, 110);
      g.lineTo(150, 0);
      g.stroke();
      break;
    }
  }
  return { map: canvas, glow };
}

const cache = new Map<string, THREE.MeshPhongMaterial>();

/** Told when an image texture arrives on a level material, so the renderer can upload it ahead of use. */
export const levelTextureEvents: { onArrive: (() => void) | null } = { onArrive: null };

/** Is this one of the shared (cached) level materials? */
export function isSharedLevelMaterial(m: THREE.Material): boolean {
  for (const c of cache.values()) if (c === m) return true;
  return false;
}

/** Shared material for a material name and tint (cached; dispose with disposeLevelMaterials). */
export function levelMaterial(
  name: MaterialName,
  color: string,
  anisotropy: number,
): THREE.MeshPhongMaterial {
  const key = name + color;
  let m = cache.get(key);
  if (m) return m;
  if (name.startsWith('img:')) {
    // Image texture (Kenney, CC0): starts as the plain tint, the image arrives a moment later.
    const id = name.slice(4);
    const mat = new THREE.MeshPhongMaterial({ color, shininess: 0, specular: 0x000000 });
    void imageTexture(id, anisotropy).then((tex) => {
      if (!tex) return;
      // Prototype textures are big grids and surfaces photos: one image per 256 units; patterns one per 128.
      const t = tex.clone();
      if (id.startsWith('proto-') || id.startsWith('surface-')) t.repeat.set(0.5, 0.5);
      t.needsUpdate = true;
      mat.map = t;
      mat.needsUpdate = true;
      levelTextureEvents.onArrive?.();
    });
    cache.set(key, mat);
    return mat;
  }
  const { map, glow } = draw(name as (typeof MATERIALS)[number], color);
  const tex = new THREE.CanvasTexture(map);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = anisotropy;
  tex.colorSpace = THREE.SRGBColorSpace;
  // No specular highlights: lights don't shine back off any surface (matte, like CS:S world brushes).
  m = new THREE.MeshPhongMaterial({
    map: tex,
    shininess: 0,
    specular: 0x000000,
    transparent: name === 'glass' || name === 'water' || name === 'wind',
    opacity: name === 'glass' ? 0.55 : name === 'water' ? 0.5 : name === 'wind' ? 0.22 : 1,
    // Fields and water are volumes you're inside of: don't let them hide what's behind.
    depthWrite: name !== 'water' && name !== 'wind',
  });
  if (glow) {
    const gt = new THREE.CanvasTexture(glow);
    gt.wrapS = gt.wrapT = THREE.RepeatWrapping;
    gt.colorSpace = THREE.SRGBColorSpace;
    m.emissiveMap = gt;
    m.emissive = new THREE.Color(0xffffff);
    m.emissiveIntensity = 1;
  }
  cache.set(key, m);
  return m;
}

export function disposeLevelMaterials() {
  for (const m of cache.values()) {
    m.map?.dispose();
    m.emissiveMap?.dispose();
    m.dispose();
  }
  cache.clear();
}

/** Small preview image of a drawn material (for the level builder's catalog). */
export function materialThumbnail(name: (typeof MATERIALS)[number], color: string, size = 72): string {
  const { map } = draw(name, color);
  const c = document.createElement('canvas');
  c.width = c.height = size;
  c.getContext('2d')!.drawImage(map, 0, 0, size, size);
  return c.toDataURL();
}
